/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { InputStageResult, OutputStageResult } from "./run.js";
import { loadRunnerState, saveRunnerState } from "./serialization.js";
export const createErrorMessage = (inputName, metadata = {}, required) => {
    const boardTitle = metadata.title ?? metadata?.url;
    const requiredText = required ? "required " : "";
    return `Missing ${requiredText}input "${inputName}"${boardTitle ? ` for board "${boardTitle}".` : "."}`;
};
export const bubbleUpInputsIfNeeded = async (metadata, context, descriptor, result, path, state = []) => {
    // If we have no way to bubble up inputs, we just return and not
    // enforce required inputs.
    if (!context.requestInput)
        return;
    const outputs = result.outputs ?? {};
    const reader = new InputSchemaReader(outputs, result.inputs, path);
    await context.state?.lifecycle().supplyPartialOutputs(outputs, path);
    if (state.length > 0) {
        const last = state[state.length - 1];
        if (last.state) {
            const unpackedState = loadRunnerState(last.state).state;
            unpackedState.partialOutputs = outputs;
            last.state = saveRunnerState("nodestart", unpackedState);
        }
    }
    result.outputs = await reader.read(createBubbleHandler(metadata, context, descriptor, state));
};
export const createBubbleHandler = (metadata, context, descriptor, state) => {
    return (async (name, schema, required, path) => {
        if (required) {
            throw new Error(createErrorMessage(name, metadata, required));
        }
        if (schema.default !== undefined) {
            if ("type" in schema && schema.type !== "string") {
                return JSON.parse(schema.default);
            }
            return schema.default;
        }
        const value = await context.requestInput?.(name, schema, descriptor, path, state);
        if (context?.signal?.aborted) {
            throw context.signal.throwIfAborted();
        }
        if (value === undefined) {
            throw new Error(createErrorMessage(name, metadata, required));
        }
        return value;
    });
};
export class InputSchemaReader {
    #currentOutputs;
    #inputs;
    #path;
    constructor(currentOutputs, inputs, path) {
        this.#currentOutputs = currentOutputs;
        this.#inputs = inputs;
        this.#path = path;
    }
    async read(handler) {
        if (!("schema" in this.#inputs))
            return this.#currentOutputs;
        const schema = this.#inputs.schema;
        if (!schema.properties)
            return this.#currentOutputs;
        const entries = Object.entries(schema.properties);
        const newOutputs = {};
        for (const [name, property] of entries) {
            if (name in this.#currentOutputs) {
                newOutputs[name] = this.#currentOutputs[name];
                continue;
            }
            const required = schema.required?.includes(name) ?? false;
            const value = await handler(name, property, required, this.#path);
            newOutputs[name] = value;
        }
        return {
            ...this.#currentOutputs,
            ...newOutputs,
        };
    }
}
export class RequestedInputsManager {
    #context;
    #cache = new Map();
    constructor(args) {
        const { inputs, ...context } = args;
        this.#context = context;
        this.#cache = new Map(inputs ? Object.entries(inputs) : []);
    }
    createHandler(next, result) {
        return async (name, schema, node, path, state) => {
            const cachedValue = this.#cache.get(name);
            if (cachedValue !== undefined)
                return cachedValue;
            const configuration = node.configuration?.schema
                ? {
                    configuration: { schema: node.configuration.schema },
                }
                : {};
            const descriptor = { id: node.id, type: node.type, ...configuration };
            const requestInputResult = {
                ...result,
                descriptor,
                inputs: {
                    schema: { type: "object", properties: { [name]: schema } },
                },
            };
            await next(new InputStageResult(requestInputResult, state, -1, path));
            const outputs = requestInputResult.outputs;
            let value = outputs && outputs[name];
            if (value === undefined) {
                value = await this.#context.requestInput?.(name, schema, descriptor, path, state);
            }
            if (!isTransient(schema)) {
                this.#cache.set(name, value);
            }
            return value;
        };
    }
}
const isTransient = (schema) => {
    return schema.behavior?.includes("transient") ?? false;
};
export const bubbleUpOutputsIfNeeded = async (outputs, descriptor, context, path) => {
    if (!context.provideOutput)
        return false;
    const schema = descriptor.configuration?.schema;
    const shouldBubble = schema?.behavior?.includes("bubble");
    if (!shouldBubble)
        return false;
    await context.provideOutput(outputs, descriptor, path);
    return true;
};
export const createOutputProvider = (next, result, context) => {
    if (context.provideOutput) {
        return context.provideOutput;
    }
    return async (outputs, descriptor, path) => {
        const provideOutputResult = {
            ...result,
            descriptor,
            inputs: outputs,
        };
        await next(new OutputStageResult(provideOutputResult, -1, path));
    };
};
//# sourceMappingURL=bubble.js.map